/**
 * Generic EIP-1193 Injected Connector
 *
 * This connector solves the postMessage cloning error (GitLab issue #4) that occurs
 * when using the @web3-react/metamask connector with non-MetaMask wallets like Rabby.
 *
 * The issue: The MetaMask connector internally uses postMessage to communicate with
 * the MetaMask extension. When a different wallet (Rabby, Brave, etc.) is the active
 * provider, this causes "Failed to execute 'postMessage' on 'Window'" errors because
 * the response objects contain non-serializable data (callbacks/functions).
 *
 * The fix: This connector directly uses the specific wallet's EIP-1193 provider
 * without going through the MetaMask-specific messaging layer.
 */

import { WalletType, getWalletProvider } from './walletDetection';

export interface EIP1193Provider {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
  on?: (event: string, handler: (...args: unknown[]) => void) => void;
  removeListener?: (
    event: string,
    handler: (...args: unknown[]) => void
  ) => void;
}

interface ConnectorState {
  isConnected: boolean;
  accounts: string[];
  chainId: number | undefined;
  walletType: WalletType;
}

interface ConnectResult {
  accounts: string[];
  chainId: number;
}

export interface InjectedConnector {
  connect: () => Promise<ConnectResult>;
  disconnect: () => void;
  getState: () => ConnectorState;
  getProvider: () => EIP1193Provider | null;
}

/**
 * Creates an injected connector for a specific wallet type.
 * This avoids the postMessage issues by using the wallet's provider directly.
 */
export function createInjectedConnector(
  walletType: WalletType
): InjectedConnector {
  let provider: EIP1193Provider | null = null;
  let state: ConnectorState = {
    isConnected: false,
    accounts: [],
    chainId: undefined,
    walletType,
  };

  // Event handlers (stored for cleanup)
  let accountsChangedHandler: ((accounts: unknown) => void) | null = null;
  let chainChangedHandler: ((chainId: unknown) => void) | null = null;

  const connect = async (): Promise<ConnectResult> => {
    // Get the specific provider for this wallet type
    provider = getWalletProvider(walletType) as EIP1193Provider | null;

    if (!provider) {
      throw new Error(
        `${walletType} wallet not found. Please make sure it's installed.`
      );
    }

    try {
      // Request accounts directly from the specific provider
      const accounts = (await provider.request({
        method: 'eth_requestAccounts',
      })) as string[];

      // Get chain ID
      const chainIdHex = (await provider.request({
        method: 'eth_chainId',
      })) as string;
      const chainId = parseInt(chainIdHex, 16);

      // Update state
      state = {
        isConnected: true,
        accounts,
        chainId,
        walletType,
      };

      // Set up event listeners
      if (provider.on) {
        accountsChangedHandler = (newAccounts: unknown) => {
          state = {
            ...state,
            accounts: newAccounts as string[],
            isConnected: (newAccounts as string[]).length > 0,
          };
        };

        chainChangedHandler = (newChainId: unknown) => {
          state = {
            ...state,
            chainId: parseInt(newChainId as string, 16),
          };
        };

        provider.on('accountsChanged', accountsChangedHandler);
        provider.on('chainChanged', chainChangedHandler);
      }

      return { accounts, chainId };
    } catch (error) {
      const err = error as { code?: number; message?: string };

      // Handle user rejection
      if (err.code === 4001 || err.message?.includes('User rejected')) {
        throw new Error('User rejected the connection request');
      }

      throw error;
    }
  };

  const disconnect = (): void => {
    // Clean up event listeners
    if (provider?.removeListener) {
      if (accountsChangedHandler) {
        provider.removeListener('accountsChanged', accountsChangedHandler);
      }
      if (chainChangedHandler) {
        provider.removeListener('chainChanged', chainChangedHandler);
      }
    }

    // Reset state
    state = {
      isConnected: false,
      accounts: [],
      chainId: undefined,
      walletType,
    };

    provider = null;
    accountsChangedHandler = null;
    chainChangedHandler = null;
  };

  const getState = (): ConnectorState => {
    return { ...state };
  };

  const getProvider = (): EIP1193Provider | null => {
    return provider;
  };

  return {
    connect,
    disconnect,
    getState,
    getProvider,
  };
}
